import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Search, Calendar, X, Mail, Settings, Shield, ArrowRightLeft, Activity, Paperclip, Archive, Trash2, RotateCcw, MoreHorizontal, IdCard, Users } from "lucide-react";
import { format } from "date-fns";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import UserMenu from "./UserMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  
  // User permissions
  const [userPermissions, setUserPermissions] = useState([]);

  const [newClient, setNewClient] = useState("");
  const [newProjectName, setNewProjectName] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");

  // Search and Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Archive / Soft-delete dialogs
  const [archiveTarget, setArchiveTarget] = useState(null);  // {project, action: 'archive'|'unarchive'}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);

  // Per-project pending attachment counts (PM at-a-glance)
  const [pendingCounts, setPendingCounts] = useState({});
  
  // Permission check helper (reactive - recalculates when userPermissions changes)
  const hasPermission = (perm) => userPermissions.includes(perm);

  useEffect(() => {
    fetchData();
    fetchUserPermissions();
    // eslint-disable-next-line
  }, []);

  // Refetch when archive filter toggles
  useEffect(() => {
    if (loading) return;
    fetchData();
    // eslint-disable-next-line
  }, [showArchived]);
  
  const fetchUserPermissions = async () => {
    try {
      const res = await axios.get(`${API}/auth/me/permissions`, getAuthHeaders());
      const perms = res.data.effective_permissions || [];
      setUserPermissions(perms);
      if (perms.includes("email.attachments.classify")) {
        fetchPendingCounts();
      }
    } catch (error) {
      console.error("Failed to load user permissions:", error);
    }
  };

  const fetchPendingCounts = async () => {
    try {
      const res = await axios.get(`${API}/email/attachments/pending-counts`, getAuthHeaders());
      setPendingCounts(res.data?.counts || {});
    } catch {
      setPendingCounts({});
    }
  };

  const fetchData = async () => {
    try {
      const url = showArchived
        ? `${API}/projects?include_archived=true`
        : `${API}/projects`;
      const [projectsRes, clientsRes] = await Promise.all([
        axios.get(url, getAuthHeaders()),
        axios.get(`${API}/clients`, getAuthHeaders()),
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const submitArchive = async () => {
    if (!archiveTarget) return;
    setActionSubmitting(true);
    try {
      const path = archiveTarget.action === "archive" ? "archive" : "unarchive";
      await axios.post(`${API}/projects/${archiveTarget.project.id}/${path}`, {}, getAuthHeaders());
      toast.success(`Project ${archiveTarget.action === "archive" ? "archived" : "unarchived"}`);
      setArchiveTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setActionSubmitting(false);
    }
  };

  const submitSoftDelete = async () => {
    if (!deleteTarget) return;
    setActionSubmitting(true);
    try {
      await axios.delete(`${API}/projects/${deleteTarget.id}`, getAuthHeaders());
      toast.success(`Project '${deleteTarget.name}' moved to trash`);
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clients`, { name: newClient }, getAuthHeaders());
      toast.success("Client created successfully");
      setShowClientDialog(false);
      setNewClient("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client");
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/projects`,
        { name: newProjectName, client_id: selectedClientId },
        getAuthHeaders()
      );
      toast.success("Project created successfully");
      setShowProjectDialog(false);
      setNewProjectName("");
      setSelectedClientId("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create project");
    }
  };

  // Helper to look up client name by client_id
  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client ? client.name : "—";
  };

  // Filter projects based on search and date range
  const filteredProjects = projects.filter((project) => {
    // Search filter (includes client name)
    const clientName = getClientName(project.client_id);
    const matchesSearch = 
      searchQuery === "" ||
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      clientName.toLowerCase().includes(searchQuery.toLowerCase());

    // Date filter
    let matchesDate = true;
    if (dateRange && (dateRange.from || dateRange.to)) {
      const projectDate = new Date(project.created_at);
      // Normalize to start of day for accurate comparison
      projectDate.setHours(0, 0, 0, 0);
      
      if (dateRange.from && dateRange.to) {
        const fromDate = new Date(dateRange.from);
        const toDate = new Date(dateRange.to);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999); // Include entire end day
        matchesDate = projectDate >= fromDate && projectDate <= toDate;
      } else if (dateRange.from) {
        const fromDate = new Date(dateRange.from);
        fromDate.setHours(0, 0, 0, 0);
        matchesDate = projectDate >= fromDate;
      } else if (dateRange.to) {
        const toDate = new Date(dateRange.to);
        toDate.setHours(23, 59, 59, 999);
        matchesDate = projectDate <= toDate;
      }
    }

    return matchesSearch && matchesDate;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setDateRange({ from: null, to: null });
  };

  const hasActiveFilters = searchQuery !== "" || (dateRange && (dateRange.from || dateRange.to));

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div data-testid="dashboard-page">
      <Toaster position="top-right" richColors />
      <div className="erp-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Project ERP System</h1>
            <p className="text-blue-100 text-sm mt-1">Logged in as {user.email} ({user.role})</p>
          </div>
          <div className="flex gap-3">
            {(hasPermission("role.view") || user.role === "ADMIN") && (
              <Button
                onClick={() => navigate("/role-management")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                data-testid="role-management-nav-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Role Management
              </Button>
            )}
            {(hasPermission("audit.view") || user.role === "ADMIN") && (
              <Button
                onClick={() => navigate("/audit")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                data-testid="audit-nav-btn"
              >
                <Activity className="w-4 h-4 mr-2" />
                Audit Log
              </Button>
            )}
            {(hasPermission("email.attachments.classify") || user.role === "ADMIN") && (
              <Button
                onClick={() => navigate("/projects/pending-attachments")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30 relative"
                data-testid="pending-attachments-nav-btn"
              >
                <Paperclip className="w-4 h-4 mr-2" />
                Pending Attachments
                {Object.values(pendingCounts).reduce((a, b) => a + b, 0) > 0 && (
                  <span
                    className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full bg-amber-400 text-amber-900"
                    data-testid="pending-attachments-total-badge"
                  >
                    {Object.values(pendingCounts).reduce((a, b) => a + b, 0)}
                  </span>
                )}
              </Button>
            )}
            {(hasPermission("project.delete") || user.role === "ADMIN") && (
              <Button
                onClick={() => navigate("/projects/trash")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                data-testid="projects-trash-nav-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Trash
              </Button>
            )}
            {(hasPermission("email.settings") || user.role === "ADMIN") && (
              <Button
                onClick={() => navigate("/email-settings")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                data-testid="email-settings-nav-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Settings
              </Button>
            )}
            {hasPermission("visitor.capture") && (
              <Button
                onClick={() => navigate("/leads")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                data-testid="leads-dashboard-btn"
              >
                <IdCard className="w-4 h-4 mr-2" />
                Leads
              </Button>
            )}
            {hasPermission("lead.view") && (
              <Button
                onClick={() => navigate("/crm")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                data-testid="crm-nav-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                CRM
              </Button>
            )}
            {hasPermission("module.safety_equipment") && (
              <Button
                onClick={() => navigate("/safety")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                data-testid="switch-module-btn"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Safety Module
              </Button>
            )}
            <UserMenu user={user} onLogout={onLogout} variant="header" />
          </div>
        </div>
      </div>

      <div className="erp-container">
        <div className="mb-6 flex gap-3">
          {(hasPermission("client.create") || ["ADMIN", "PROJECT_WRITE", "APPROVER"].includes(user.role)) && (
            <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
              <DialogTrigger asChild>
                <Button className="erp-btn erp-btn-primary" data-testid="create-client-btn">
                  Create Client
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Client</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateClient}>
                  <div className="mb-4">
                    <Label>Client Name</Label>
                    <Input
                      value={newClient}
                      onChange={(e) => setNewClient(e.target.value)}
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-client-btn">
                    Create Client
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {(hasPermission("project.create") || ["ADMIN", "PROJECT_WRITE", "APPROVER"].includes(user.role)) && (
            <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
              <DialogTrigger asChild>
                <Button className="erp-btn erp-btn-primary" data-testid="create-project-btn">
                  Create Project
                </Button>
              </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Project</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateProject}>
                    <div className="mb-4">
                      <Label>Project Name</Label>
                      <Input
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        required
                        data-testid="project-name-input"
                      />
                    </div>
                    <div className="mb-4">
                      <Label>Client</Label>
                      <Select value={selectedClientId} onValueChange={setSelectedClientId} required>
                        <SelectTrigger data-testid="client-select">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-project-btn">
                      Create Project
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
          )}
        </div>

        {/* Control Bar - Search and Filters */}
        <div className="erp-card mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search by project name, ID, or client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10"
                data-testid="search-projects-input"
              />
            </div>

            {/* Date Range Filter */}
            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-10 border-dashed ${
                    (dateRange && (dateRange.from || dateRange.to)) ? "bg-blue-50 border-blue-300" : ""
                  }`}
                  data-testid="date-filter-button"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {dateRange && dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "MMM dd")} - {format(dateRange.to, "MMM dd, yyyy")}
                      </>
                    ) : (
                      <>From {format(dateRange.from, "MMM dd, yyyy")}</>
                    )
                  ) : (
                    "Date Range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="p-3 border-b">
                  <p className="text-sm font-medium">Select Date Range</p>
                </div>
                <DayPicker
                  mode="range"
                  selected={dateRange || undefined}
                  onSelect={(range) => setDateRange(range || { from: null, to: null })}
                  numberOfMonths={2}
                  className="p-3"
                />
                <div className="p-3 border-t flex justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDateRange({ from: null, to: null })}
                    data-testid="clear-date-filter"
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowDatePicker(false)}
                    data-testid="apply-date-filter"
                  >
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-10"
                data-testid="clear-all-filters"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <div className="mt-3 flex flex-wrap gap-2">
              {searchQuery && (
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-200">
                  Search: "{searchQuery}"
                  <button
                    onClick={() => setSearchQuery("")}
                    className="ml-1 hover:bg-blue-100 rounded"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              {(dateRange && (dateRange.from || dateRange.to)) && (
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-200">
                  Date: {dateRange.from ? format(dateRange.from, "MMM dd") : "Start"} - {dateRange.to ? format(dateRange.to, "MMM dd, yyyy") : "End"}
                  <button
                    onClick={() => setDateRange({ from: null, to: null })}
                    className="ml-1 hover:bg-blue-100 rounded"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="erp-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Projects</h2>
            {(hasPermission("project.archive") || user.role === "ADMIN") && (
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid="show-archived-toggle">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="w-4 h-4"
                />
                Show archived
              </label>
            )}
          </div>
          {filteredProjects.length === 0 ? (
            <p className="text-gray-500" data-testid="no-projects-message">
              {projects.length === 0 
                ? "No projects yet. Create your first project." 
                : "No projects match your filters."}
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Showing {filteredProjects.length} of {projects.length} projects
              </p>
              <table className="erp-table" data-testid="projects-table">
                <thead>
                  <tr>
                    <th>Project ID</th>
                    <th>Project Name</th>
                    <th>Client</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => {
                    const canArchive = hasPermission("project.archive") || user.role === "ADMIN";
                    const canDelete = hasPermission("project.delete") || user.role === "ADMIN";
                    const showMenu = canArchive || canDelete;
                    return (
                    <tr key={project.id} data-testid={`project-row-${project.id}`}>
                      <td className="font-mono text-sm">{project.id}</td>
                      <td className="font-semibold">
                        {project.name}
                        {project.is_archived && (
                          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded border border-amber-200" data-testid={`archived-badge-${project.id}`}>
                            <Archive className="w-3 h-3" /> Archived
                          </span>
                        )}
                        {pendingCounts[project.id] > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/projects/pending-attachments?project_id=${project.id}`); }}
                            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-800 text-xs rounded border border-amber-200 hover:bg-amber-100 transition"
                            title="Click to classify pending attachments"
                            data-testid={`pending-att-badge-${project.id}`}
                          >
                            <Paperclip className="w-3 h-3" />
                            {pendingCounts[project.id]} pending
                          </button>
                        )}
                      </td>
                      <td className="text-gray-600" data-testid={`project-client-${project.id}`}>{getClientName(project.client_id)}</td>
                      <td>
                        <span className="status-badge status-approved">{project.status}</span>
                      </td>
                      <td className="text-gray-600">
                        {new Date(project.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => navigate(`/project/${project.id}`)}
                            variant="outline"
                            size="sm"
                            data-testid={`view-project-btn-${project.id}`}
                          >
                            View Details
                          </Button>
                          {showMenu && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`project-menu-btn-${project.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canArchive && !project.is_archived && (
                                  <DropdownMenuItem
                                    onClick={() => setArchiveTarget({ project, action: "archive" })}
                                    data-testid={`archive-menu-${project.id}`}
                                  >
                                    <Archive className="w-4 h-4 mr-2" /> Archive
                                  </DropdownMenuItem>
                                )}
                                {canArchive && project.is_archived && (
                                  <DropdownMenuItem
                                    onClick={() => setArchiveTarget({ project, action: "unarchive" })}
                                    data-testid={`unarchive-menu-${project.id}`}
                                  >
                                    <RotateCcw className="w-4 h-4 mr-2" /> Unarchive
                                  </DropdownMenuItem>
                                )}
                                {canDelete && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => setDeleteTarget(project)}
                                      className="text-red-600"
                                      data-testid={`delete-menu-${project.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" /> Move to Trash
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* Archive / Unarchive confirm */}
      <Dialog open={!!archiveTarget} onOpenChange={(o) => { if (!o) setArchiveTarget(null); }}>
        <DialogContent data-testid="archive-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {archiveTarget?.action === "archive" ? "Archive project" : "Unarchive project"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700">
            {archiveTarget?.action === "archive"
              ? <>Archived projects are hidden from the default list and toggle back on with "Show archived". All data (POs, files, emails) is preserved.</>
              : <>This project will reappear in the default list.</>
            }
          </p>
          <p className="font-semibold mt-2">{archiveTarget?.project?.name}</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setArchiveTarget(null)} disabled={actionSubmitting} data-testid="archive-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitArchive} disabled={actionSubmitting} data-testid="archive-confirm-btn">
              {archiveTarget?.action === "archive" ? "Archive" : "Unarchive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Soft-delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Move to Trash</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700">
            This will move the project to Trash. It can be restored or permanently purged later by a Super Admin.
          </p>
          <p className="font-semibold mt-2">{deleteTarget?.name}</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={actionSubmitting} data-testid="delete-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submitSoftDelete}
              disabled={actionSubmitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-btn"
            >
              Move to Trash
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Dashboard;
